/**
 * Setup Builder — /pages/setup-builder (sections/setup-builder.liquid).
 *
 * The visitor picks a venue format, gets our complete setup as a starting point, then swaps
 * positions and fills the rows we left open. What he ends up with is a spec — the thing he asks
 * us to quote.
 *
 * Four data sources, none of them a second copy of the catalog:
 *   • the format picker                 — server-rendered from generated blocks with the hub's own
 *                                         `setup-card`, so the two never drift apart
 *   • assets/setup-<slug>.builder.json  — one format's composition (handles, leaf, priority, qty)
 *   • Section Rendering API             — a leaf's products, live, with prices and MOQ
 *   • /products/<handle>.js             — title + price for a preset line the visitor has not
 *                                         opened the category of yet
 *
 * The category tree is READ OUT OF THE DOM (snippets/category-tree, rendered hidden by the
 * section). It already knows every leaf, its title and which zone it belongs to, and it already
 * hides empty collections. Parsing it beats minting another copy of the taxonomy that would go
 * stale the first time a leaf is renamed.
 *
 * State lives in localStorage and in the URL FRAGMENT (never the query string — see the section
 * comment). One crawlable URL, no matter how many specs exist.
 */
(function () {
  // Storefront root of the locale being browsed — `/` or `/de/`, always trailing-slashed.
  // A bare '/products/…' fetch answers from the primary locale, so a visitor in /de would get
  // English titles and the default market's prices.
  var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var root = document.querySelector('[data-builder]');
  if (!root) return;

  var PRESETS = {
    must: ['must'],
    must_recommended: ['must', 'recommended'],
    all: ['must', 'recommended', 'optional'],
  };

  var el = {
    start: root.querySelector('[data-builder-start]'),
    formats: root.querySelector('[data-builder-formats]'),
    blank: root.querySelector('[data-builder-blank]'),
    work: root.querySelector('[data-builder-work]'),
    presets: root.querySelector('[data-builder-presets]'),
    checklist: root.querySelector('[data-builder-checklist]'),
    checklistToggle: root.querySelector('[data-builder-checklist-toggle]'),
    checklistLabel: root.querySelector('[data-builder-checklist-label]'),
    paneTitle: root.querySelector('[data-builder-pane-title]'),
    paneHint: root.querySelector('[data-builder-pane-hint]'),
    paneBack: root.querySelector('[data-builder-pane-back]'),
    products: root.querySelector('[data-builder-products]'),
    summary: root.querySelector('[data-builder-summary]'),
    count: root.querySelector('[data-builder-count]'),
    total: root.querySelector('[data-builder-total]'),
    bar: root.querySelector('[data-builder-bar]'),
    barCount: root.querySelector('[data-builder-bar-count]'),
    barTotal: root.querySelector('[data-builder-bar-total]'),
    specList: root.querySelector('[data-builder-spec-list]'),
    addBtn: root.querySelector('[data-builder-add-to-spec]'),
    saveBtn: root.querySelector('[data-builder-save]'),
    saved: root.querySelector('[data-builder-saved]'),
    search: root.querySelector('[data-builder-search]'),
    searchClear: root.querySelector('[data-builder-search-clear]'),
    quoteOpen: root.querySelector('[data-builder-quote-open]'),
    modal: root.querySelector('[data-builder-quote-modal]'),
    modalClose: root.querySelector('[data-builder-quote-close]'),
    modalSetup: root.querySelector('[data-builder-modal-setup]'),
    modalCount: root.querySelector('[data-builder-modal-count]'),
    modalTotal: root.querySelector('[data-builder-modal-total]'),
    quoteForm: root.querySelector('[data-builder-quote-form]'),
    quoteSuccess: root.querySelector('[data-builder-quote-success]'),
    quoteError: root.querySelector('[data-builder-quote-error]'),
    quoteRetry: root.querySelector('[data-builder-quote-retry]'),
    confirm: root.querySelector('[data-builder-confirm]'),
    confirmYes: root.querySelector('[data-builder-confirm-yes]'),
    confirmNo: root.querySelector('[data-builder-confirm-no]'),
    tree: root.querySelector('[data-builder-tree]'),
    view: root.querySelector('[data-bp-view-modal]'),
    viewClose: root.querySelector('[data-bp-view-close]'),
    viewTrack: root.querySelector('[data-bp-view-track]'),
    viewPrev: root.querySelector('[data-bp-view-prev]'),
    viewNext: root.querySelector('[data-bp-view-next]'),
    viewTitle: root.querySelector('[data-bp-view-title]'),
    viewPrice: root.querySelector('[data-bp-view-price]'),
    viewBadges: root.querySelector('[data-bp-view-badges]'),
    viewDesc: root.querySelector('[data-bp-view-desc]'),
    viewAdd: root.querySelector('[data-bp-view-add]'),
    viewLink: root.querySelector('[data-bp-view-link]'),
  };

  // Money format and currency are NOT read here — assets/spec-store.js owns them, so this page and
  // /cart cannot print a total two ways.
  var cfg = {
    indexUrl: root.dataset.indexUrl,
    productsSection: root.dataset.productsSection,
  };

  /**
   * Sibling asset URL, derived from the one real asset URL the section hands us.
   *
   * `data-index-url` looks like `//cdn.shopify.com/…/assets/setups-index.json?v=123`. Splitting on
   * the filename gives the assets prefix and the `?v=` suffix; the suffix comes along on purpose —
   * these files are rewritten by every dashboard push, and without it the CDN would happily serve
   * the composition from before the setup was changed.
   */
  function assetUrl(name) {
    var parts = cfg.indexUrl.split('setups-index.json');
    return parts.length === 2 ? parts[0] + name + parts[1] : name;
  }

  // The builder has its OWN list (owner, 2026-08-06): this page is where somebody tries a setup on
  // for size, and it must not touch — let alone wipe — the spec he has already decided on. What he
  // assembles here reaches the spec only through «Add to spec».
  //
  // Same store implementation as the spec (assets/spec-store.js), a different key and a different
  // event. This page holds a REFERENCE to its state object and adds the two things only the builder
  // has: the venue format it was started from and the checklist of categories to close.
  //
  // { setup, preset, rows: [leaf…], items: { key: line }, dirty, owned } — `rows` is the checklist,
  // which outlives the items in it: emptying a row must not delete the row, that is the whole point
  // of a checklist. `dirty` = the visitor edited the list by hand since the preset was applied, and
  // is what the confirm dialog asks about before a switch throws that away. `owned` = the saved
  // spec THIS browser may rewrite.
  //
  // ⚠️ Never reassign `state` — mutate it. The store hands out this one object.
  var Spec = window.BQSpecStore.create({ key: 'bq_setup_v1', event: 'setup:updated' });
  window.BQSetup = Spec;
  var state = Spec.state;

  var leaves = {}; // handle -> { title, zone, zoneTitle, groupTitle }
  var activeRow = null;

  /**
   * True while the tiles on screen have just been drawn. A product with variants opens itself when
   * one of them enters the spec — but a grid that arrives half-unfolded is not a grid to compare
   * in, so on the drawing pass every tile stays closed (owner, 2026-08-06). Cleared by the first
   * syncPane over the new markup.
   */
  var paneFresh = true;

  /**
   * On a phone the checklist is a folded menu and the pane opens on the spec itself — the two
   * columns are one, and the visitor has to see what he is assembling before he sees a catalog.
   * Same breakpoint as the stylesheet's; the two must not drift.
   */
  var narrow = window.matchMedia('(max-width: 900px)');
  function isNarrow() { return narrow.matches; }
  var savedCode = null;

  /* ---------------------------------------------------------------- utilities */

  // The spec's own vocabulary comes from the store, so a name printed here and the same name on
  // /cart cannot read differently.
  var titleize = Spec.titleize;

  function leafTitle(handle) {
    return (leaves[handle] && leaves[handle].title) || titleize(handle);
  }

  function t(key, fallback) {
    // Strings the JS needs are rendered by the section into a hidden [data-i18n] block, so they
    // live in locales/ like every other string in the theme rather than in a table in here.
    var node = root.querySelector('[data-i18n="' + key + '"]');
    return node ? node.textContent : fallback;
  }

  /* -------------------------------------------------------------- persistence */

  // Storage, the saved-copy API and the `setup:updated` event all belong to the store now — this
  // page only says WHEN the list changed, never how it is kept.
  var save = Spec.save;

  function readFragment() {
    var params = new URLSearchParams(location.hash.slice(1));
    return { setup: params.get('setup'), preset: params.get('preset'), code: params.get('s') };
  }

  function writeFragment() {
    var params = new URLSearchParams();
    if (state.setup) params.set('setup', state.setup);
    if (state.preset) params.set('preset', state.preset);
    var next = params.toString();
    // replaceState, not location.hash: a preset change is not a place in history to go back to,
    // and it must never scroll the page to an element that happens to match the id.
    history.replaceState(null, '', next ? '#' + next : location.pathname);
  }

  /* ---------------------------------------------------------- taxonomy from DOM */

  /**
   * A zone and a group carry their own name in `summary > .cat-tree__link`, while a LEAF carries
   * it in `.cat-tree__name`. Reading `.cat-tree__name` off a zone therefore returns the name of
   * its first leaf — which is how the «add a category» list ended up filing thirty categories
   * under «Cocktail & novelty glasses».
   */
  function ownName(node) {
    if (!node) return null;
    var link = node.querySelector(':scope > details > summary > .cat-tree__link') ||
      node.querySelector(':scope > .cat-tree__link');
    return link ? link.textContent.trim() : null;
  }

  function parseTree() {
    if (!el.tree) return;
    var nodes = el.tree.querySelectorAll('[data-node="leaf"]');
    Array.prototype.forEach.call(nodes, function (node) {
      var handle = node.getAttribute('data-handle');
      if (!handle || leaves[handle]) return;
      var name = node.querySelector('.cat-tree__name');
      var group = node.closest('[data-node="group"]');
      var zone = node.closest('[data-node="zone"]');
      // Grouped by GROUP where there is one (Glassware, Furniture, Lighting…), by zone otherwise.
      // A zone is far too coarse to pick from: `guest-area` alone holds 34 leaves.
      leaves[handle] = {
        title: name ? name.textContent.trim() : titleize(handle),
        zone: zone ? zone.getAttribute('data-handle') : null,
        zoneTitle: ownName(zone),
        groupTitle: ownName(group),
      };
    });
  }

  /* ------------------------------------------------------------- format picker */

  /**
   * The picker itself is server-rendered (the hub's own `setup-card`, from generated blocks), so
   * there is nothing to build here — only to read the titles back out for the header and for
   * restoring a saved session.
   */
  function formatTitle(slug) {
    var card = el.formats && el.formats.querySelector('[data-format="' + slug + '"]');
    return card ? card.getAttribute('data-format-title') : null;
  }

  /* ------------------------------------------------------------ setup → checklist */

  function loadSetup(slug) {
    // The URL comes from the picker card, where Liquid stamped it with that FILE's version.
    // Deriving it from the index's URL reused the index's `?v=`, which does not change when only
    // a composition does — and the CDN then serves the copy cached before the change.
    var card = el.formats && el.formats.querySelector('[data-format="' + slug + '"]');
    var url = (card && card.getAttribute('data-format-asset')) ||
      assetUrl('setup-' + encodeURIComponent(slug) + '.builder.json');
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  /**
   * Checklist rows out of a setup: every leaf its positions touch, in zone order, deduplicated.
   * The row is what has to be closed; the setup's own picks are what it starts closed WITH.
   */
  function rowsFromSetup(spec) {
    var rows = [];
    var seen = {};
    (spec.zones || []).forEach(function (zone) {
      (zone.items || []).forEach(function (item) {
        var leaf = item.category;
        if (!leaf || seen[leaf]) return;
        seen[leaf] = true;
        rows.push({ leaf: leaf, zone: zone.slug || null, zoneTitle: zone.heading || '' });
      });
    });
    return rows;
  }

  function applyPreset(spec, preset) {
    var wanted = PRESETS[preset] || PRESETS.must;
    var items = {};
    (spec.zones || []).forEach(function (zone) {
      (zone.items || []).forEach(function (item) {
        if (wanted.indexOf(item.priority) === -1) return;
        // The composition names a variant for anything sold in more than one — key the line by it,
        // so the row opens on a concrete size instead of «the product, unspecified».
        items[keyOf(item.handle, item.variant_id)] = {
          handle: item.handle,
          variant_id: item.variant_id || null,
          category: item.category,
          priority: item.priority,
          qty: item.qty || 1,
          // The supplier minimum, as the composition asset states it — the generator emits it
          // precisely because /products/<handle>.js cannot. It used to be the setup's own QUANTITY,
          // on the reasoning that the composition already respects MOQ; it does, but that is a
          // different statement, and it pinned «10 booths» at ten and an LED screen whose minimum
          // is one at five. Absent = 1, and the live row still corrects it in markCell.
          moq: item.moq || 1,
          from_preset: true,
          title: null,
          price: null,
        };
      });
    });
    state.items = items;
    // Applying a preset IS the reset — from here the list is ours again, not the visitor's.
    state.dirty = false;
  }

  /**
   * Fill in title and price for lines we only know by handle — preset lines, and lines restored
   * from a saved link. The reading itself lives in the store (one implementation, shared with
   * /cart); what belongs here is redrawing the three things this page shows as answers arrive.
   */
  function hydrate(keys) {
    return Spec.hydrate(keys, {
      onTick: function () {
        renderSummary();
        renderChecklist();
        syncPane();
      },
    });
  }

  /* ------------------------------------------------------------------ rendering */

  function itemsOf(leaf) {
    return Object.keys(state.items).filter(function (handle) {
      return state.items[handle].category === leaf;
    });
  }

  /**
   * Fold the checklist. Only the phone layout shows the toggle, but the attribute is set on both —
   * the desktop stylesheet keeps the panel visible regardless, so there is no branch here.
   */
  function setChecklistOpen(open) {
    if (!el.checklistToggle) return;
    el.checklist.hidden = !open;
    el.checklistToggle.setAttribute('aria-expanded', String(open));
  }

  function renderChecklist() {
    if (!el.checklist) return;

    // The folded menu says what it would open into: the category being browsed, or the word for
    // all of them. A menu labelled only «Categories» while a category is open is a lie.
    if (el.checklistLabel) {
      el.checklistLabel.textContent = activeRow
        ? leafTitle(activeRow)
        : t('categories', 'Categories');
    }

    el.checklist.innerHTML = '';

    // «Add a category» stands at the TOP of the column, level with the catalog beside it, and the
    // list grows UNDER it (owner, 2026-08-06). A button at the bottom of forty rows is a button
    // nobody scrolls to; and the line under it says what the rows are for.
    el.checklist.appendChild(buildAddRow());

    var hint = document.createElement('p');
    hint.className = 'builder-checklist__hint';
    hint.textContent = t('checklist_hint', 'Pick the category you need.');
    el.checklist.appendChild(hint);

    var byZone = [];
    var index = {};
    state.rows.forEach(function (row) {
      var key = zoned() ? (row.zone || '_') : '_';
      if (!index[key]) {
        index[key] = {
          title: zoned() ? (row.zoneTitle || titleize(row.zone) || '') : '',
          rows: [],
        };
        byZone.push(index[key]);
      }
      index[key].rows.push(row);
    });

    byZone.forEach(function (zone) {
      var wrap = document.createElement('div');
      wrap.className = 'builder-zone';
      if (zone.title) {
        var head = document.createElement('h4');
        head.className = 'builder-zone__title';
        head.textContent = zone.title;
        wrap.appendChild(head);
      }

      var list = document.createElement('ul');
      list.className = 'builder-rows';
      zone.rows.forEach(function (row) {
        var filled = itemsOf(row.leaf).length;
        var li = document.createElement('li');
        li.className = 'builder-rows__item';

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'builder-row';
        btn.setAttribute('data-filled', String(filled));
        btn.setAttribute('aria-current', String(activeRow === row.leaf));

        var name = document.createElement('span');
        name.textContent = leafTitle(row.leaf);
        var stateEl = document.createElement('span');
        stateEl.className = 'builder-row__state';
        stateEl.textContent = filled ? '✓ ' + filled : '—';

        btn.appendChild(name);
        btn.appendChild(stateEl);
        btn.addEventListener('click', function () { openLeaf(row.leaf); });

        // The × sits BEFORE the name, exactly where it sits on a product in the spec (owner,
        // 2026-08-06), and it is disabled — shown, not hidden — while the category holds picks: a
        // row with products in it would take them with it, and those go one at a time by their own
        // ×. Greyed out it says «not this way»; missing it would say «this row cannot be dropped».
        li.appendChild(buildDropRow(row.leaf, filled));
        li.appendChild(btn);

        list.appendChild(li);
      });

      wrap.appendChild(list);
      el.checklist.appendChild(wrap);
    });
  }

  /**
   * The × on a checklist row. Our setups cover a venue in general, and a given venue does not need
   * every line of one — a row nobody is going to fill is noise in the list and a false «not covered
   * yet» in the spec. Live only while the row is empty (see the call site).
   */
  function buildDropRow(leaf, filled) {
    var drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'builder-row__drop icon-button';
    var label = t('remove_category', 'Remove this category') + ': ' + leafTitle(leaf);
    drop.setAttribute('aria-label', label);
    drop.title = label;
    drop.disabled = !!filled;

    var icon = root.querySelector('[data-builder-icon-close]');
    if (icon) drop.appendChild(icon.content.cloneNode(true));

    drop.addEventListener('click', function () { removeRow(leaf); });
    return drop;
  }

  function removeRow(leaf) {
    var wasOpen = activeRow === leaf;
    state.rows = state.rows.filter(function (row) { return row.leaf !== leaf; });
    touch();
    save();
    if (wasOpen) activeRow = null;
    renderChecklist();
    renderSummary();

    // Only if the pane was showing the row that just left. A search, or another category, keeps
    // what it had — the list lost a line, not the visitor's place in the catalog.
    if (wasOpen) restorePane();
  }

  /**
   * Plain substring, anywhere in the title. Scrolling a hundred-odd leaves is the work the field
   * removes, and a leaf is usually remembered by a word in the middle of its name — «marie» has to
   * find «Chafing dishes & bain marie», «fryer» has to find «Deep fryers».
   */
  function titleMatches(title, query) {
    return String(title).toLowerCase().indexOf(query) !== -1;
  }

  /**
   * «Add a category» — the whole tree, so a format can be taken past what our setup covers.
   *
   * A listbox we build, not a `<select>`: a native dropdown cannot be filtered (typing in one only
   * jumps to the first letter), and the tree is long enough that scrolling it is the slow part.
   */
  function buildAddRow() {
    var wrap = document.createElement('div');
    wrap.className = 'builder-zone builder-add';

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'builder-add__toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = t('add_category', '+ Add a category');

    var panel = document.createElement('div');
    panel.className = 'builder-add__panel';
    panel.hidden = true;

    var field = document.createElement('input');
    field.type = 'search';
    field.className = 'input builder-add__search';
    field.autocomplete = 'off';
    field.placeholder = t('search_categories', 'Type to filter');
    field.setAttribute('aria-label', field.placeholder);

    var list = document.createElement('div');
    list.className = 'builder-add__list';
    list.setAttribute('role', 'listbox');

    var empty = document.createElement('p');
    empty.className = 'builder-add__empty';
    empty.hidden = true;
    empty.textContent = t('search_empty_categories', 'No category matches.');

    var used = {};
    state.rows.forEach(function (row) { used[row.leaf] = true; });

    var groups = {};
    Object.keys(leaves).forEach(function (handle) {
      if (used[handle]) return;
      var label = leaves[handle].groupTitle || leaves[handle].zoneTitle || '';
      if (!groups[label]) groups[label] = [];
      groups[label].push(handle);
    });

    // [{ head: <element>, options: [<element>…] }] — kept so filtering hides a heading the moment
    // its last option goes, instead of leaving a group label over nothing.
    var sections = [];

    Object.keys(groups).sort().forEach(function (groupTitle) {
      var head = document.createElement('p');
      head.className = 'builder-add__group';
      head.textContent = groupTitle || '—';
      list.appendChild(head);

      var options = [];
      groups[groupTitle].sort(function (a, b) { return leafTitle(a).localeCompare(leafTitle(b)); });
      groups[groupTitle].forEach(function (handle) {
        var option = document.createElement('button');
        option.type = 'button';
        option.className = 'builder-add__option';
        option.setAttribute('role', 'option');
        option.setAttribute('data-leaf', handle);
        option.textContent = leafTitle(handle);
        option.addEventListener('click', function () { addRow(handle); });
        list.appendChild(option);
        options.push(option);
      });

      sections.push({ head: head, options: options });
    });

    function addRow(leaf) {
      state.rows.push({
        leaf: leaf,
        zone: leaves[leaf] ? leaves[leaf].zone : null,
        zoneTitle: leaves[leaf] ? leaves[leaf].zoneTitle : null,
      });
      save();
      renderChecklist();
      openLeaf(leaf);
    }

    function filter() {
      var query = field.value.trim().toLowerCase();
      var shown = 0;
      sections.forEach(function (section) {
        var visible = 0;
        section.options.forEach(function (option) {
          var hit = query === '' || titleMatches(option.textContent, query);
          option.hidden = !hit;
          if (hit) visible += 1;
        });
        section.head.hidden = visible === 0;
        shown += visible;
      });
      empty.hidden = shown > 0;
    }

    function setOpen(open) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        field.value = '';
        filter();
        field.focus();
      }
    }

    toggle.addEventListener('click', function () { setOpen(panel.hidden); });
    field.addEventListener('input', filter);

    field.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        setOpen(false);
        toggle.focus();
        return;
      }
      // Enter takes the top of what is left — the whole point of narrowing the list by typing.
      if (event.key === 'Enter') {
        event.preventDefault();
        var first = list.querySelector('.builder-add__option:not([hidden])');
        if (first) first.click();
      }
    });

    panel.appendChild(field);
    panel.appendChild(list);
    panel.appendChild(empty);
    wrap.appendChild(toggle);
    wrap.appendChild(panel);
    return wrap;
  }

  /**
   * Whether the list is filed under zone headings at all.
   *
   * Zones are the SETUP's own structure — the checklist inherits them from the composition it was
   * started from, and the spec reads in the same blocks as the page it came from. Started blank
   * there is no venue and no composition: a leaf still knows a zone from the taxonomy, but printing
   * «Bar & service» over two rows the visitor picked by hand files his list under a plan he never
   * made. So a spec with no setup behind it is one flat list. (Owner, 2026-08-05.)
   */
  function zoned() {
    return !!state.setup;
  }

  /** Which zone a leaf belongs to — the checklist first, the tree for anything added past it. */
  function zoneOf(leaf) {
    if (!zoned()) return { key: '_', title: '' };
    for (var i = 0; i < state.rows.length; i += 1) {
      if (state.rows[i].leaf === leaf) {
        return {
          key: state.rows[i].zone || '_',
          title: state.rows[i].zoneTitle || titleize(state.rows[i].zone) || '',
        };
      }
    }
    if (leaves[leaf]) {
      return { key: leaves[leaf].zone || '_', title: leaves[leaf].zoneTitle || '' };
    }
    return { key: '_', title: '' };
  }

  /**
   * The spec, split by zone the way a complete setup is.
   *
   * ONLY what the visitor picked. Empty checklist rows used to be listed here too, as muted «not
   * covered yet» lines — the reasoning being that the question before sending is «what have I
   * missed». It reads the other way round: a spec that lists rows nobody chose looks like a form
   * with unfinished fields, and the only way to make it stop nagging is to delete each row by hand.
   * What is still open belongs to the checklist, which is where the visitor goes to fill it.
   *
   * Zone order follows the checklist, which follows the setup's own composition, so the spec reads
   * in the same order as the page it was started from.
   */
  function specGroups() {
    var order = [];
    var index = {};

    function ensure(zone) {
      if (!index[zone.key]) {
        index[zone.key] = { title: zone.title, items: [] };
        order.push(index[zone.key]);
      }
      return index[zone.key];
    }

    // Rows first, so a zone keeps the position the checklist gives it rather than the order its
    // first pick happened to be added in.
    state.rows.forEach(function (row) {
      ensure(zoneOf(row.leaf));
    });

    Object.keys(state.items).forEach(function (key) {
      ensure(zoneOf(state.items[key].category)).items.push(key);
    });

    return order.filter(function (group) { return group.items.length; });
  }

  /**
   * The spec, into whichever container is showing it — the totals card, and on a phone the pane
   * itself. The rows themselves are drawn by assets/spec-table.js: /cart shows the SAME list, and a
   * second renderer here would drift from that one the first time a column changes.
   *
   * What this page adds is the filing: zones of the setup the spec was started from, so it reads in
   * the same blocks as the page it came from. A spec started blank has none — see zoned().
   */
  function renderSpecInto(container) {
    window.BQSpecTable.render(container, {
      store: Spec,
      groups: specGroups().map(function (group) {
        return { title: group.title, keys: group.items };
      }),
    });
  }

  function renderSummary() {
    if (!el.summary) return;
    var totals = Spec.totals();
    el.summary.hidden = totals.count === 0;

    // Just the number: the row it sits in is labelled «Positions», the same card /cart shows.
    el.count.textContent = String(totals.count);
    // «from» while any line is still unpriced — a total that silently omits lines would read as
    // the price of the whole spec — and equally while any line is made-to-order: its own price is
    // only the base build, so the sum that contains it cannot be final either. The rule lives in
    // the store, so this total and /cart's cannot disagree.
    el.total.textContent = window.BQSpecTable.totalLabel(Spec);

    // The phone's bar carries the same two numbers — it exists because the card they live in is a
    // scroll below the products. Hidden along with the card when there is nothing to total.
    if (el.bar) el.bar.hidden = totals.count === 0;
    if (el.barCount) el.barCount.textContent = totals.count + ' ' + t('positions', 'positions');
    if (el.barTotal) el.barTotal.textContent = el.total.textContent;

    renderSpecInto(el.specList);

    // The modal's card describes what is being quoted — kept in step here so it is never opened
    // showing a total the visitor has already changed.
    if (el.modalSetup) {
      el.modalSetup.textContent = state.setup
        ? (formatTitle(state.setup) || titleize(state.setup))
        : t('blank_title', 'Your spec');
    }
    if (el.modalCount) el.modalCount.textContent = totals.count + ' ' + t('positions', 'positions');
    if (el.modalTotal) el.modalTotal.textContent = el.total.textContent;
  }

  /* -------------------------------------------------------------- product pane */

  /**
   * Nothing open yet — say what to do with the column instead of leaving it blank. Starting from
   * scratch used to land here silently, and (worse) kept showing the products of whatever category
   * was open before, so an empty spec looked like it already had a category picked.
   */
  function showPrompt() {
    activeRow = null;
    renderChecklist();
    if (el.paneBack) el.paneBack.hidden = true;
    el.paneTitle.textContent = t('pick_a_row', 'Pick a category');
    el.paneHint.textContent = '';

    el.products.innerHTML = '';
    var hint = document.createElement('p');
    hint.className = 'builder-pane__empty';
    hint.textContent = t(
      'pane_empty',
      'Pick a category to see the equipment that fits it, or search the full catalog above.'
    );
    el.products.appendChild(hint);
  }

  function openLeaf(leaf) {
    activeRow = leaf;
    renderChecklist();
    // On a phone the menu has done its job — leaving it open would push the products it just
    // loaded off the screen.
    if (isNarrow()) setChecklistOpen(false);
    if (el.paneBack) el.paneBack.hidden = false;
    el.paneTitle.textContent = leafTitle(leaf);
    el.paneHint.textContent = '';
    el.products.innerHTML = '<p class="builder-row__state">…</p>';

    fetch(localeRoot + 'collections/' + encodeURIComponent(leaf) + '?section_id=' + encodeURIComponent(cfg.productsSection), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        el.products.innerHTML = html;
        wirePane(leaf);
      })
      .catch(function () {
        el.products.innerHTML = '<p class="builder-row__state">' +
          t('pane_error', 'Could not load this category. Try again.') + '</p>';
      });
  }

  /**
   * Search the WHOLE catalog, not the open category. Rendered through our product-row section, so a
   * result looks and behaves exactly like a tile in a category — same Add button, same stepper,
   * same live price and MOQ.
   *
   * ⚠️ The storefront SEARCH, not `/search/suggest` (owner, 2026-08-06). Predictive search caps
   * every resource at ten and matches title, vendor, product type and variant titles only, so
   * «beer» found ten products and quietly dropped the rest. `/search` reads descriptions and tags
   * too and paginates, which is what «Show more» under the grid pages through. `options[prefix]`
   * keeps a half-typed last word matching, the way the field behaved before.
   */
  function runSearch(term) {
    var url = localeRoot + 'search?q=' + encodeURIComponent(term) +
      '&type=product&options%5Bprefix%5D=last&section_id=' + encodeURIComponent(cfg.productsSection);

    if (el.paneBack) el.paneBack.hidden = !isNarrow();
    el.paneTitle.textContent = t('search_results', 'Search results');
    el.paneHint.textContent = term;
    el.products.innerHTML = '<p class="builder-row__state">…</p>';

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        el.products.innerHTML = html;
        if (!el.products.querySelector('[data-bp-row]')) {
          el.products.innerHTML = '<p class="builder-row__state">' +
            t('search_empty', 'Nothing matched.') + '</p>';
          return;
        }
        // `null` leaf: a result carries no category of its own here, so the line takes the leaf
        // of the row it came from — resolved in addItem from the product's own data.
        wirePane(null);
      })
      .catch(function () {
        el.products.innerHTML = '<p class="builder-row__state">' +
          t('pane_error', 'Could not search right now. Try again.') + '</p>';
      });
  }

  /* ------------------------------------------------- choosing a size, quick view */

  /**
   * The size popup of ONE tile. Never two at once: it covers the window, and a second one under it
   * is a click that appears to do nothing.
   */
  function setPickerOpen(row, open) {
    if (open) closePopups();
    var picker = row.querySelector('[data-bp-picker]');
    if (!picker) return;
    picker.hidden = !open;
    var choose = row.querySelector(':scope > [data-bp-action] [data-bp-choose]');
    if (choose) choose.setAttribute('aria-expanded', String(open));
  }

  /** Shut whatever is open over the grid — the size popup, the quick view, or both. */
  function closePopups() {
    if (!el.products) return;
    Array.prototype.forEach.call(el.products.querySelectorAll('[data-bp-picker]'), function (picker) {
      if (picker.hidden) return;
      picker.hidden = true;
      var row = picker.closest('[data-bp-row]');
      var choose = row && row.querySelector(':scope > [data-bp-action] [data-bp-choose]');
      if (choose) choose.setAttribute('aria-expanded', 'false');
    });
    if (el.view) el.view.hidden = true;
  }

  /**
   * The quick view's photos, as a strip you slide sideways. The arrows appear only when there is
   * something to slide TO — on a single-photo product they would be two buttons that do nothing.
   */
  function setGallery(images) {
    if (!el.viewTrack) return;
    el.viewTrack.innerHTML = '';
    (images || []).forEach(function (src) {
      var img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      el.viewTrack.appendChild(img);
    });
    el.viewTrack.scrollLeft = 0;

    var many = (images || []).length > 1;
    if (el.viewPrev) el.viewPrev.hidden = !many;
    if (el.viewNext) el.viewNext.hidden = !many;
  }

  function slideGallery(direction) {
    if (!el.viewTrack) return;
    // Most of a screenful, not exactly one — the strip is scrolled, not paged, so the arrows move
    // it along and leave the edge of the next photo in sight.
    el.viewTrack.scrollBy({ left: direction * el.viewTrack.clientWidth * 0.7, behavior: 'smooth' });
  }

  /**
   * Quick view: what the product IS, without leaving the list being assembled (owner, 2026-08-06).
   *
   * Opens instantly on what the tile already knows — title, price, picture — and fills in the
   * description when `/products/<handle>.js` answers, the same live read every price here comes
   * from. The footer offers the tile's own two moves and nothing else: this is a workbench, and
   * «buy» is not one of them.
   */
  function openQuickView(row, leaf) {
    if (!el.view) return;
    closePopups();

    var handle = row.getAttribute('data-handle');
    var hasVariants = !!row.querySelector('[data-bp-picker]');
    var custom = row.getAttribute('data-customizable') === 'true';
    var moq = parseInt(row.getAttribute('data-moq'), 10) || 1;
    var price = parseInt(row.getAttribute('data-price'), 10);

    el.viewTitle.textContent = row.getAttribute('data-title') || titleize(handle);
    el.viewPrice.textContent = isFinite(price)
      ? (custom ? t('price_from', 'from') + ' ' : '') + Spec.money(price)
      : '';
    // Opens on the thumbnail the tile already has, so there is a picture before the catalog answers.
    setGallery([row.getAttribute('data-image')].filter(Boolean));
    el.viewLink.href = row.getAttribute('data-url') || (localeRoot + 'products/' + handle);
    el.viewDesc.textContent = t('view_loading', 'Loading…');

    el.viewBadges.innerHTML = '';
    function badge(text, modifier) {
      var span = document.createElement('span');
      span.className = 'bp-view__badge' + (modifier ? ' bp-view__badge--' + modifier : '');
      span.textContent = text;
      el.viewBadges.appendChild(span);
    }
    if (moq > 1) badge(t('moq_hint', 'MOQ =') + ' ' + moq);
    if (custom) badge(t('customizable', 'Custom order'), 'custom');

    // One button, the tile's own: a product with sizes cannot be added as «the product», so it
    // hands over to the size popup instead of guessing one.
    el.viewAdd.textContent = hasVariants ? t('choose', 'Choose') : t('add', 'Add');
    el.viewAdd.onclick = function () {
      el.view.hidden = true;
      if (hasVariants) setPickerOpen(row, true);
      else addItem(row, leaf, null);
    };

    el.view.hidden = false;

    fetch(localeRoot + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (product) {
        // The visitor may have closed it, or opened another one, while this was in the air.
        if (!product || el.view.hidden || el.viewTitle.textContent !== product.title) return;
        el.viewDesc.innerHTML = product.description || '';
        setGallery(product.images && product.images.length
          ? product.images
          : [product.featured_image].filter(Boolean));
      })
      .catch(function () {
        el.viewDesc.textContent = '';
      });
  }

  // Escape shuts whichever popup is on top, like every other modal here.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closePopups();
  });

  if (el.view) {
    el.view.addEventListener('click', function (event) {
      if (event.target === el.view) el.view.hidden = true;
    });
    if (el.viewClose) {
      el.viewClose.addEventListener('click', function () { el.view.hidden = true; });
    }
    if (el.viewPrev) el.viewPrev.addEventListener('click', function () { slideGallery(-1); });
    if (el.viewNext) el.viewNext.addEventListener('click', function () { slideGallery(1); });
  }

  function wirePane(leaf) {
    // Everything below was just put on screen — including whatever «Show more» appended — so the
    // sync at the end of this function must not unfold anything.
    paneFresh = true;

    var rows = el.products.querySelectorAll('[data-bp-row]');
    Array.prototype.forEach.call(rows, function (row) {
      // «Show more» appends into the same list and re-wires the pane, so a tile that is already
      // listening must be left alone — a second handler on its Add button would put the product in
      // the spec twice on one click.
      if (row.hasAttribute('data-bp-wired')) return;
      row.setAttribute('data-bp-wired', '');

      // Single-variant product: the row's own Add button. The querySelector is scoped to the
      // action cell so it cannot pick up the Add button of a variant inside the row.
      var cell = row.querySelector(':scope > [data-bp-action]');
      var btn = cell && cell.querySelector('[data-bp-add]');
      if (btn) {
        btn.addEventListener('click', function () { addItem(row, leaf, null); });
      }

      var choose = cell && cell.querySelector('[data-bp-choose]');
      var picker = row.querySelector('[data-bp-picker]');
      if (choose && picker) {
        choose.addEventListener('click', function () { setPickerOpen(row, picker.hidden); });
      }

      if (picker) {
        var close = picker.querySelector('[data-bp-picker-close]');
        if (close) close.addEventListener('click', function () { setPickerOpen(row, false); });
        // The dimmed area around the card is the way out, as in every other modal on this site.
        picker.addEventListener('click', function (event) {
          if (event.target === picker) setPickerOpen(row, false);
        });

        Array.prototype.forEach.call(picker.querySelectorAll('[data-bp-variant]'), function (v) {
          var add = v.querySelector('[data-bp-add]');
          if (add) add.addEventListener('click', function () { addItem(row, leaf, v); });
        });
      }

      // The picture and the name are links to the product page — taken here so a visitor mid-list
      // gets the quick view instead of a new tab. Modified clicks (new tab, new window) are left
      // to the browser: that is what the href is for.
      Array.prototype.forEach.call(row.querySelectorAll('[data-bp-view]'), function (trigger) {
        trigger.addEventListener('click', function (event) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          openQuickView(row, leaf);
        });
      });
    });
    syncPane();

    var next = el.products.querySelector('[data-bp-next]');
    // …and the same goes for the pager itself: it lives inside `[data-bp-next]`, which survives the
    // re-wire, so building a second one would leave two buttons under the grid.
    if (next && !next.firstElementChild) {
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'btn btn--transparent';
      more.textContent = t('more', 'Show more');
      more.addEventListener('click', function () {
        more.disabled = true;
        fetch(next.getAttribute('data-bp-next') + '&section_id=' + encodeURIComponent(cfg.productsSection), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var list = el.products.querySelector('.bp__list');
            var incoming = doc.querySelectorAll('[data-bp-row]');
            Array.prototype.forEach.call(incoming, function (row) { list.appendChild(row); });
            var nextNext = doc.querySelector('[data-bp-next]');
            if (nextNext) {
              next.setAttribute('data-bp-next', nextNext.getAttribute('data-bp-next'));
              more.disabled = false;
            } else {
              more.remove();
            }
            wirePane(leaf);
          })
          .catch(function () { more.disabled = false; });
      });
      next.appendChild(more);
    }
  }

  // A spec line is a PRODUCT — or one of its variants, once the visitor picks one: `handle`, or
  // `handle::<variantId>`. The rule is the store's, so a position added here and the same position
  // added from its product page are one line, not two.
  var keyOf = Spec.keyOf;

  /** Mark the spec as hand-edited. Only a preset application clears this. */
  var touch = Spec.touch;

  /**
   * Ask before throwing away hand-picked changes. Resolves true when there is nothing to lose, or
   * when the visitor says go ahead. `<dialog>` rather than window.confirm: same look as the quote
   * modal, and readable on a phone.
   */
  function confirmReset() {
    if (!state.dirty || !el.confirm) return Promise.resolve(true);
    return new Promise(function (resolve) {
      function done(answer) {
        el.confirm.close();
        el.confirmYes.removeEventListener('click', yes);
        el.confirmNo.removeEventListener('click', no);
        el.confirm.removeEventListener('cancel', no);
        resolve(answer);
      }
      function yes() { done(true); }
      function no() { done(false); }
      el.confirmYes.addEventListener('click', yes);
      el.confirmNo.addEventListener('click', no);
      el.confirm.addEventListener('cancel', no);
      el.confirm.showModal();
    });
  }

  /**
   * Re-read one action cell from state: a quantity stepper while its line is in the spec, the Add
   * button otherwise. Everything that changes the spec ends up here, which is what the preset
   * switch was missing — it re-applied the preset but left the open category showing the previous
   * answer.
   */
  function markCell(cell, key, moqFloor) {
    if (!cell) return;
    var stepper = cell.querySelector('[data-bp-stepper]');
    var line = state.items[key];

    if (!line) {
      // The Add button never left the cell — it is only hidden by CSS while the line is in the
      // spec, so there is nothing to rebuild here.
      cell.removeAttribute('data-bp-in');
      if (stepper) stepper.remove();
      return;
    }

    cell.setAttribute('data-bp-in', '');

    // A preset line, and one restored from a saved link, carries no MOQ of its own — this row is
    // where the real supplier minimum first becomes known. Adopt it, and lift a quantity that sits
    // under it: learning the minimum is exactly the moment a number we cannot ship has to stop
    // being one. Converges after one pass — the next call finds the floor already set.
    if (moqFloor > 0 && Spec.setMoq(key, moqFloor)) renderSummary();

    if (!stepper) {
      stepper = buildStepper(key);
      cell.appendChild(stepper);
    }
    syncStepper(stepper, line);
  }

  function buildStepper(key) {
    var wrap = document.createElement('div');
    wrap.className = 'bp__stepper';
    wrap.setAttribute('data-bp-stepper', '');

    // The left button is BOTH controls, one at a time (owner, 2026-08-06): a minus while there is
    // something to take away, a pink × once the quantity is down to the minimum we can ship. A
    // disabled minus next to a separate bin was two dead-looking buttons saying one thing —
    // «this is as few as it goes» — without offering the move that actually follows: drop it.
    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'bp__step';
    minus.setAttribute('data-bp-less', '');

    var qty = document.createElement('span');
    qty.className = 'bp__qty';

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'bp__step';
    plus.textContent = '+';
    plus.setAttribute('aria-label', t('more_qty', 'More'));

    minus.addEventListener('click', function () {
      var line = state.items[key];
      if (!line) return;
      // Read at click time, not wired at build time: the same button changes meaning as the
      // quantity moves, and the line it belongs to is redrawn under it.
      if ((line.qty || 1) <= (line.moq || 1)) removeItem(key);
      else bumpQty(key, -1);
    });
    plus.addEventListener('click', function () { bumpQty(key, 1); });

    wrap.appendChild(minus);
    wrap.appendChild(qty);
    wrap.appendChild(plus);
    return wrap;
  }

  function syncStepper(stepper, line) {
    var floor = line.moq || 1;
    stepper.querySelector('.bp__qty').textContent = String(line.qty);

    // At the minimum the minus turns into the × that drops the position — below MOQ is a quantity
    // we cannot ship, so «one fewer» is not a move that exists there; «none at all» is.
    var less = stepper.querySelector('[data-bp-less]');
    var atFloor = line.qty <= floor;
    less.textContent = atFloor ? '×' : '−';
    less.classList.toggle('bp__step--drop', atFloor);
    less.setAttribute('aria-label', atFloor ? t('remove', 'Remove') : t('less', 'Fewer'));

    stepper.setAttribute('data-moq', String(floor));
    // «MOQ = 50», not «Supplier minimum: 50» — the trade term is what the buyer already reads on
    // every quote, and it carries the operator so the number lands where the string puts it.
    stepper.title = floor > 1 ? t('moq_hint', 'MOQ =') + ' ' + floor : '';
  }

  // The quantity rules (the MOQ floor included) belong to the store; the redraw that follows is
  // this page's, and it arrives through `setup:updated` — see the subscription at the bottom.
  var bumpQty = Spec.bump;

  /** Re-read every visible row from state. Called by anything that touches the spec. */
  function syncPane() {
    if (!el.products) return;
    var rows = el.products.querySelectorAll('[data-bp-row]');
    Array.prototype.forEach.call(rows, function (row) {
      var handle = row.getAttribute('data-handle');
      var moq = parseInt(row.getAttribute('data-moq'), 10) || 1;
      var cell = row.querySelector(':scope > [data-bp-action]');
      var variants = row.querySelector('[data-bp-variants]');

      if (!variants) {
        markCell(cell, keyOf(handle, null), moq);
        row.toggleAttribute('data-bp-in', !!state.items[keyOf(handle, null)]);
        return;
      }

      var chosen = 0;
      Array.prototype.forEach.call(variants.querySelectorAll('[data-bp-variant]'), function (v) {
        var key = keyOf(handle, v.getAttribute('data-variant-id'));
        markCell(v.querySelector('[data-bp-action]'), key, moq);
        if (state.items[key]) chosen += 1;
      });
      // A line with no variant can only come from an older saved spec — the composition names one
      // now. It still counts as «this product is in», and it takes over the row's control: the
      // Choose button steps aside rather than sitting next to a stepper, which is what stacked the
      // two on top of each other.
      var bare = state.items[keyOf(handle, null)];
      if (bare) chosen += 1;
      markCell(cell, keyOf(handle, null), moq);

      var badge = row.querySelector('[data-bp-chosen]');
      if (badge) {
        badge.hidden = chosen === 0;
        badge.textContent = String(chosen);
      }

      var picker = row.querySelector('[data-bp-picker]');

      // A product whose variant is already in the spec opens itself — but NOT on the pass that
      // draws the grid (owner, 2026-08-06). A tile that unfolds the moment a category opens
      // stretches its whole row of tiles, and the visitor is comparing products, not reading back
      // a size he picked earlier; the badge on «Choose» already says how many are in.
      //
      // Otherwise only on the way IN (the row had no line a moment ago), so a list the visitor
      // collapsed by hand does not spring open again every time he changes a quantity somewhere.
      var wasIn = row.hasAttribute('data-bp-in');
      if (chosen > 0 && !wasIn && !paneFresh && picker) setPickerOpen(row, true);

      row.toggleAttribute('data-bp-in', chosen > 0);
    });

    // Whatever comes next is a change the visitor made while looking at this grid, so from here on
    // a row that gains its first variant may unfold itself.
    paneFresh = false;
  }

  function addItem(row, leaf, variantEl) {
    var handle = row.getAttribute('data-handle');
    // Added from search, the row knows its own leaf; browsing, the open checklist row does.
    var category = leaf || row.getAttribute('data-category') || null;
    var moq = parseInt(row.getAttribute('data-moq'), 10) || 1;
    var variantId = variantEl ? variantEl.getAttribute('data-variant-id') : null;

    // A product added from search may belong to a row the checklist does not have yet — add the
    // row, so the spec never holds a position that the list above it cannot account for.
    if (category && !state.rows.some(function (r) { return r.leaf === category; })) {
      state.rows.push({
        leaf: category,
        zone: leaves[category] ? leaves[category].zone : null,
        zoneTitle: leaves[category] ? leaves[category].zoneTitle : null,
      });
    }

    // Replacing a line that names no size, and carrying its quantity and brief over, is the
    // store's rule — see the store's `add`.
    Spec.add({
      handle: handle,
      category: category,
      moq: moq,
      qty: moq,
      customizable: row.getAttribute('data-customizable') === 'true',
      title: row.getAttribute('data-title'),
      variant_id: variantId,
      variant_title: variantEl ? variantEl.getAttribute('data-variant-title') : null,
      // A variant's own price, not the product's «from» price — that is the whole point of
      // choosing one.
      price: parseInt((variantEl || row).getAttribute('data-price'), 10),
      image: row.getAttribute('data-image'),
      url: row.getAttribute('data-url'),
    });
  }

  var removeItem = Spec.remove;

  /* ------------------------------------------------------------------- screens */

  /**
   * «Selected setup: X» + «Change setup» belong to the page header, so they are written into the
   * hero's slot (sections/hero.liquid, `enable_slot`) rather than floating above the checklist.
   * The slot lives OUTSIDE this section, hence `document`, not `root`.
   */
  var heroSlot = document.querySelector('[data-hero-slot]');

  function renderHeroSlot(title) {
    if (!heroSlot) return;
    heroSlot.innerHTML = '';
    heroSlot.hidden = !title;
    if (!title) return;

    var label = document.createElement('span');
    label.className = 'hero__slot-label';
    label.textContent = t('selected_setup', 'Selected setup');

    var value = document.createElement('strong');
    value.className = 'hero__slot-value';
    value.textContent = title;

    var change = document.createElement('button');
    change.type = 'button';
    change.className = 'hero__slot-link';
    change.textContent = t('change_format', 'Change setup');
    change.addEventListener('click', changeSetup);

    heroSlot.appendChild(label);
    heroSlot.appendChild(value);
    heroSlot.appendChild(change);
  }

  function changeSetup() {
    confirmReset().then(function (ok) {
      if (!ok) return;
      state.setup = null;
      writeFragment();
      showStart();
    });
  }

  function showStart() {
    el.start.hidden = false;
    el.work.hidden = true;
    renderHeroSlot(null);
  }

  function showWork(title) {
    el.start.hidden = true;
    el.work.hidden = false;
    // The preset picks a slice OF A COMPOSITION — «must-have», «everything». Started from scratch
    // there is no composition to slice, so the three tiles are a control that cannot do anything.
    if (el.presets) el.presets.hidden = !state.setup;
    renderHeroSlot(title);
    // The panel ships with `hidden` so a phone starts folded before the script runs; a wide screen
    // has no toggle to unfold it, so it is opened here as well as by the stylesheet.
    setChecklistOpen(!isNarrow());
    renderChecklist();
    renderSummary();
    // Land on the first row of the checklist — the pane is the catalog on both breakpoints now, and
    // a setup with rows has an obvious first one to open. An empty list gets the prompt instead of
    // the leftovers of whatever setup the visitor was looking at.
    if (state.rows.length) openLeaf(state.rows[0].leaf);
    else showPrompt();
  }

  function chooseFormat(slug) {
    loadSetup(slug)
      .then(function (spec) {
        state.setup = slug;
        state.rows = rowsFromSetup(spec);
        applyPreset(spec, state.preset);
        // A different venue is a different spec, not a new version of the saved one — the link
        // already sent must not turn into somebody else's setup.
        state.owned = null;
        savedCode = null;
        if (el.saved) { el.saved.hidden = true; el.saved.textContent = ''; }
        save();
        writeFragment();
        showWork(formatTitle(slug) || spec.title);
        hydrate(Object.keys(state.items));
      })
      .catch(function () {
        showStart();
      });
  }

  function startBlank() {
    state.setup = null;
    state.rows = [];
    state.items = {};
    state.dirty = false;
    state.owned = null;
    savedCode = null;
    if (el.saved) { el.saved.hidden = true; el.saved.textContent = ''; }
    save();
    writeFragment();
    showWork(t('blank_title', 'Your spec'));
  }

  /* ---------------------------------------------------------------------- boot */

  parseTree();

  Array.prototype.forEach.call(root.querySelectorAll('[data-builder-preset]'), function (input) {
    input.addEventListener('change', function () {
      var previous = state.preset;
      // Re-applying a preset wipes hand-picked changes by design — the preset IS the starting
      // point. That is worth a question when there is something to lose.
      confirmReset().then(function (ok) {
        if (!ok) {
          var back = root.querySelector('[data-builder-preset][value="' + previous + '"]');
          if (back) back.checked = true;
          return;
        }
        state.preset = input.value;
        writeFragment();
        if (!state.setup) return;
        loadSetup(state.setup).then(function (spec) {
          applyPreset(spec, state.preset);
          save();
          renderChecklist();
          renderSummary();
          // The open category must answer for the NEW preset immediately — leaving it showing the
          // previous answer until the visitor clicks another row is the bug the owner caught.
          syncPane();
          hydrate(Object.keys(state.items));
        });
      });
    });
  });

  if (el.blank) {
    el.blank.addEventListener('click', function () {
      confirmReset().then(function (ok) { if (ok) startBlank(); });
    });
  }

  /* ---------------------------------------------------------------- search */

  /** Back to whatever the pane showed before the search — a category, or the prompt to pick one. */
  function restorePane() {
    if (activeRow) {
      openLeaf(activeRow);
      return;
    }
    showPrompt();
  }

  if (el.search) {
    var searchTimer = null;
    el.search.addEventListener('input', function () {
      var term = el.search.value.trim();
      el.searchClear.hidden = term === '';
      if (searchTimer) clearTimeout(searchTimer);
      if (term.length < 2) {
        // Too short to mean anything — put the category back rather than searching for «a».
        if (term === '') restorePane();
        return;
      }
      searchTimer = setTimeout(function () { runSearch(term); }, 250);
    });
  }

  if (el.searchClear) {
    el.searchClear.addEventListener('click', function () {
      el.search.value = '';
      el.searchClear.hidden = true;
      restorePane();
    });
  }

  /* ------------------------------------------------- narrow-screen navigation */

  // Clicking outside closes the category picker. ONE handler for the life of the page: buildAddRow
  // runs on every checklist render, and binding it there would leave a listener per render holding
  // on to a panel that is no longer in the document.
  document.addEventListener('click', function (event) {
    var open = root.querySelector('.builder-add__panel:not([hidden])');
    if (!open || open.parentNode.contains(event.target)) return;
    open.hidden = true;
    var toggle = open.parentNode.querySelector('.builder-add__toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  });

  if (el.checklistToggle) {
    el.checklistToggle.addEventListener('click', function () {
      setChecklistOpen(el.checklist.hidden);
    });
  }

  // Back out of an open category to the categories themselves — the menu, unfolded, which on a
  // phone is the thing the category was reached through. It used to lead to the spec; the spec is
  // now at the bottom of the page and is scrolled to, not navigated to.
  if (el.paneBack) {
    el.paneBack.addEventListener('click', function () {
      if (el.search && el.search.value) {
        el.search.value = '';
        el.searchClear.hidden = true;
      }
      showPrompt();
      setChecklistOpen(true);
    });
  }

  // Rotating a phone, or dragging a desktop window narrow, changes which of the two layouts the
  // page is in — the checklist is a folded menu on one and an open column on the other. The pane
  // shows the catalog either way, so nothing about it needs re-pointing.
  var onBreakpoint = function () {
    if (el.work.hidden) return;
    setChecklistOpen(!isNarrow());
  };
  if (narrow.addEventListener) narrow.addEventListener('change', onBreakpoint);
  else if (narrow.addListener) narrow.addListener(onBreakpoint);

  /* ------------------------------------------------------------ save + quote */

  // Saving a spec, the link it produces and the clipboard trick behind the copy all live in
  // assets/spec-store.js — /cart saves the same list through the same endpoints, and two copies of
  // «rewrite the spec this browser owns» would be two chances to strand a link already sent.
  var specLines = Spec.specLines;
  var specPayload = Spec.specPayload;
  var saveSpec = Spec.saveSpec;
  var specLink = Spec.specLink;
  var copyWhileActive = Spec.copyWhileActive;

  /**
   * The saved link, shown as something you can grab by hand. Even with the trick above a browser
   * may refuse the clipboard (permissions, an embedded webview), and a link the visitor cannot
   * select is worse than no button at all.
   */
  function renderSavedLink(link, copied) {
    el.saved.textContent = '';

    var note = document.createElement('span');
    note.className = 'builder-total__saved-note';
    note.textContent = copied
      ? t('saved_hint', 'Link copied.')
      : t('saved_hint_manual', 'Saved. Copy the link:');

    var field = document.createElement('input');
    field.type = 'text';
    field.className = 'input builder-total__saved-link';
    field.value = link;
    field.readOnly = true;
    field.addEventListener('focus', function () { field.select(); });

    var copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'btn btn--transparent builder-total__saved-copy';
    copy.textContent = t('copy', 'Copy');
    copy.addEventListener('click', function () {
      // Straight out of a click, so this is the one call that always has activation behind it.
      field.select();
      copyWhileActive(Promise.resolve(link)).then(function (ok) {
        copy.textContent = ok ? t('copied', 'Copied') : t('copy', 'Copy');
      });
    });

    el.saved.appendChild(note);
    el.saved.appendChild(field);
    el.saved.appendChild(copy);

    // A refused clipboard is invisible: the browser keeps whatever was copied BEFORE, so the
    // visitor pastes an older link and has every reason to believe the button lied to him. Pre-
    // selecting the field makes the correct link one Ctrl+C away instead of a drag with the mouse.
    if (!copied) field.select();
  }

  /**
   * «Add to spec» — the one road from this page to the list the visitor is actually carrying.
   *
   * The two lists are separate on purpose (see the store): here he is trying a venue on, there he
   * has decided. So this copies rather than moves — the setup stays put, and the spec is never
   * emptied by anything that happens on this page.
   *
   * A position already in the spec is RAISED to whatever the setup asks for, never added on top:
   * pressing the button twice must not order two of everything.
   */
  function addSetupToSpec() {
    var cart = window.BQSpec;
    if (!cart) return 0;

    var moved = 0;
    Object.keys(state.items).forEach(function (key) {
      var line = state.items[key];
      var existing = cart.line(key);
      moved += 1;

      if (existing) {
        if ((line.qty || 1) > (existing.qty || 1)) cart.setQty(key, line.qty);
        return;
      }

      cart.add({
        handle: line.handle,
        variant_id: line.variant_id || null,
        variant_title: line.variant_title || null,
        title: line.title || null,
        price: typeof line.price === 'number' ? line.price : null,
        image: line.image || null,
        url: line.url || null,
        category: line.category || null,
        priority: line.priority || null,
        moq: line.moq || 1,
        qty: line.qty || line.moq || 1,
        customizable: !!line.customizable,
        note: line.note || null,
        from_preset: !!line.from_preset,
      });
    });

    return moved;
  }

  if (el.addBtn) {
    el.addBtn.addEventListener('click', function () {
      if (!addSetupToSpec()) return;
      window.BQSpec.toast();
      // Prices this page never learned (a preset line the visitor never opened) are read live in
      // the spec, so /cart opens on real numbers rather than dashes.
      window.BQSpec.hydrate();
    });
  }

  if (el.saveBtn) {
    el.saveBtn.addEventListener('click', function () {
      el.saveBtn.disabled = true;

      var linkPromise = saveSpec().then(function (code) {
        if (!code) throw new Error('spec not saved');
        savedCode = code;
        return specLink(code);
      });

      // Started HERE, inside the click — not in the .then below.
      var copying = copyWhileActive(linkPromise);

      linkPromise.then(function (link) {
        copying.then(function (ok) {
          el.saveBtn.disabled = false;
          el.saved.hidden = false;
          renderSavedLink(link, ok);
        });
      }, function () {
        el.saveBtn.disabled = false;
        el.saved.hidden = false;
        el.saved.textContent = t('save_error', 'Could not save right now.');
      });
    });
  }

  if (el.quoteOpen && el.modal) {
    el.quoteOpen.addEventListener('click', function () {
      // Reset any state left from a previous attempt, so «Request a quote» always opens a form.
      el.quoteSuccess.hidden = true;
      el.quoteError.hidden = true;
      el.quoteForm.hidden = false;
      el.modal.showModal();
    });
  }

  if (el.modalClose && el.modal) {
    el.modalClose.addEventListener('click', function () { el.modal.close(); });
  }

  // Turnstile hooks, named to sit alongside the setup page's own (assets/setup-quote-form.js).
  // Token is read at submit time and the button is never disabled waiting for it — the
  // contract lives in assets/lead-submit.js. The callbacks only clear the notice.
  function turnstileToken() {
    return el.quoteForm ? window.bqLeadToken(el.quoteForm) : '';
  }

  function clearQuoteHint() {
    if (el.quoteForm && turnstileToken()) window.bqLeadHint(el.quoteForm, false);
  }

  window.onBuilderTurnstileVerify = clearQuoteHint;
  window.onBuilderTurnstileExpired = clearQuoteHint;
  window.onBuilderTurnstileError = clearQuoteHint;

  if (el.quoteRetry) {
    el.quoteRetry.addEventListener('click', function () {
      el.quoteError.hidden = true;
      el.quoteForm.hidden = false;
    });
  }

  if (el.quoteForm) {
    el.quoteForm.addEventListener('submit', function (event) {
      event.preventDefault();

      // No token yet: show the reason rather than doing nothing. Checked BEFORE the
      // button goes into its submitting state, so a blocked submit leaves the form
      // exactly as it was.
      if (el.modal.dataset.turnstileRequired === 'true' && !turnstileToken()) {
        window.bqLeadHint(el.quoteForm, true);
        return;
      }
      window.bqLeadHint(el.quoteForm, false);

      // Disabled only for the duration of the request (re-enabled below), so a double
      // click can't send two inquiries. This is not a Turnstile gate.
      var submit = el.quoteForm.querySelector('[data-builder-quote-submit]');
      if (submit) {
        submit.disabled = true;
        if (submit.dataset.labelSubmitting) submit.textContent = submit.dataset.labelSubmitting;
      }

      // Save the spec first so the lead carries a code we can open, not just a flattened list.
      // A failed save must not cost us the inquiry — the positions ride in `details` either way.
      saveSpec().then(function (code) {
        if (code) savedCode = code;
        var spec = specPayload();
        var lines = specLines();
        // The customization briefs, flattened into one readable line — «Обращения» prints a
        // detail value as text, and a brief nobody reads is a brief nobody wrote.
        var briefs = lines
          .filter(function (entry) { return entry.item.note; })
          .map(function (entry) {
            return (entry.line.title || entry.item.handle) +
              (entry.item.qty > 1 ? ' ×' + entry.item.qty : '') + ': ' + entry.item.note;
          });
        // Everything the builder knows that a plain input can't carry; the contact
        // fields, attribution and Turnstile token are added by bqBuildLeadPayload.
        var extra = {
          spec_code: code || null,
          spec_url: code ? specLink(code) : null,
          setup_slug: spec.setup_slug,
          preset: spec.preset,
          positions: spec.items.length,
          total: spec.totals.subtotal,
          currency: spec.totals.currency,
          // Which surface the spec was sent from — this page and /cart share `source`, so the
          // inbox and the Make email have no other way to tell them apart (assets/spec-page.js
          // sends `cart` from the same slot).
          origin: 'builder',
          // What the Обращения inbox shows in the «interest» column (lead-meta.ts::interestOf) and
          // the only spec line the Make email prints — so the surface is named here too, in plain
          // English: it is read by us, not shown to a visitor, and the Make scenario stays untouched.
          spec_summary:
            'Builder — ' +
            (state.setup ? (formatTitle(state.setup) || state.setup) + ' — ' : '') +
            spec.items.length + ' ' + t('positions', 'positions'),
          customization: briefs.length ? briefs.join(' | ') : null,
          items: lines.map(function (entry) {
            return {
              handle: entry.item.handle,
              title: entry.line.title || entry.item.handle,
              qty: entry.item.qty,
              priority: entry.item.priority,
              note: entry.item.note,
            };
          }),
        };

        var payload = window.bqBuildLeadPayload(el.quoteForm, 'builder_quote', extra);

        return window.bqSubmitLead(el.modal.dataset.leadsUrl, payload).then(function (ok) {
          if (!ok) throw new Error('lead failed');
          el.quoteForm.hidden = true;
          el.quoteError.hidden = true;
          el.quoteSuccess.hidden = false;
        });
      }).catch(function () {
        el.quoteForm.hidden = true;
        el.quoteError.hidden = false;
      }).then(function () {
        if (submit) submit.disabled = false;
      });
    });
  }

  // The picker's cards are plain links to `#setup=…`, so a click lands here rather than in a
  // handler bound to each card — which also covers the «Change this setup» link arriving from a
  // setup page and the back/forward buttons.
  window.addEventListener('hashchange', function () {
    var next = readFragment();
    if (next.setup && next.setup !== state.setup) {
      // Picking another format throws the current list away, so it asks first — same guard as the
      // preset radios. Saying no puts the fragment back, otherwise the URL would claim a format
      // the screen is not showing.
      confirmReset().then(function (ok) {
        if (!ok) {
          writeFragment();
          return;
        }
        if (next.preset && PRESETS[next.preset]) state.preset = next.preset;
        chooseFormat(next.setup);
      });
      return;
    }
    if (next.preset && PRESETS[next.preset]) state.preset = next.preset;
    if (!next.setup && state.setup) {
      state.setup = null;
      showStart();
    }
  });

  // Anything that touches the spec — a stepper in the table, a × on a row, a position added from
  // a product page in another tab — redraws the three things this page shows. The mutations
  // themselves live in the store; what is left here is the answer to «it changed».
  document.addEventListener('setup:updated', function () {
    if (el.work.hidden) return;
    renderSummary();
    renderChecklist();
    syncPane();
  });

  var fragment = readFragment();
  // The state the store loaded out of localStorage, as it was at boot: a `#setup=` or `#s=` in the
  // URL is the more recent intent and outranks it.
  var saved = Spec.stored;

  if (fragment.preset && PRESETS[fragment.preset]) {
    state.preset = fragment.preset;
  } else if (saved && saved.preset && PRESETS[saved.preset]) {
    state.preset = saved.preset;
  }
  var presetInput = root.querySelector('[data-builder-preset][value="' + state.preset + '"]');
  if (presetInput) presetInput.checked = true;

  if (fragment.code) {
    // A saved link wins over everything: it is someone opening a spec that was sent to him, and
    // silently showing him his own last session instead would be the wrong list entirely.
    Spec.fetchSpec(fragment.code)
      .then(function (spec) {
        if (!spec) throw new Error('not found');
        savedCode = spec.code;
        // Rows come back with the zone they belong to, read out of the tree this page has parsed —
        // the store knows handles, not the taxonomy.
        Spec.applySpec(spec, function (leaf) {
          return {
            zone: leaves[leaf] ? leaves[leaf].zone : null,
            zoneTitle: leaves[leaf] ? leaves[leaf].zoneTitle : null,
          };
        });
        showWork((state.setup && formatTitle(state.setup)) || t('blank_title', 'Your spec'));
        // Prices and titles are NOT restored from the saved spec — they are re-read live, so an
        // old link never quotes yesterday's price back at the visitor.
        hydrate(Object.keys(state.items));
      })
      .catch(function () {
        showStart();
      });
  } else if (fragment.setup) {
    // A link into a format always wins over what is in storage — it is the more recent intent, and
    // it is how the «Change this setup» button on a setup page arrives.
    chooseFormat(fragment.setup);
  } else if (saved && ((saved.rows || []).length || Object.keys(saved.items || {}).length)) {
    // Positions without rows are a spec picked off product pages and listing cards — it opens the
    // builder on the list it already has, not on the format picker.
    showWork((state.setup && formatTitle(state.setup)) || t('blank_title', 'Your spec'));
    writeFragment();
    // Only lines that never learned a price — the ones added from a product page came with theirs.
    hydrate(Object.keys(state.items));
  } else {
    showStart();
  }
})();
